import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { useApiQuery } from '@/api/ApiQueryServiceProvider'
import { Button } from '@/components/ui/button'
import { Loader2, ArrowLeft, MapPin, Users, BarChart2, Map } from 'lucide-react'
import { ProfileHeader, ProfileSection } from '@/components/sensnet/ProfileLayout'
import type { GetNetworkCorridorResponse, NetworkCorridor } from '@/types/sensNet'
import type { GetOrganizationsResponse } from '@/types/organizations'
import { AppLogger } from '@/lib/logger/appLogger'

const log = AppLogger.getLogger(import.meta)

interface NetworkCorridorDetailProps {
  corridorNumber: number
}

export function NetworkCorridorDetail({ corridorNumber }: NetworkCorridorDetailProps) {
  const { data, isLoading, isError } = useApiQuery<GetNetworkCorridorResponse>(
    'getNetworkCorridorByNumber',
    { networkCorridorNumber: corridorNumber }
  )
  const { data: orgsData } = useApiQuery<GetOrganizationsResponse>('getOrganizations', {})

  const corridor: NetworkCorridor | undefined = useMemo(() => {
    const payload: any = (data as any)?.content?.networkCorridor || (data as any)?.networkCorridor || (data as any)?.corridor || data
    log.info('NetworkCorridorDetail: raw API response', {
      hasData: !!data,
      keys: data ? Object.keys(data) : [],
      payloadKeys: payload ? Object.keys(payload) : [],
      mediaLocations: (payload as any)?.mediaLocations,
    })
    if (!payload) return undefined
    const normalizedNumber = payload.networkCorridorNumber ?? payload.corridorNumber
    if (normalizedNumber !== undefined && Number(normalizedNumber) !== corridorNumber) {
      return undefined
    }

    return {
      id: payload.id,
      networkCorridorNumber: normalizedNumber,
      name: payload.networkCorridorName || payload.name || '',
      description: payload.description || '',
      countiesCovered: payload.countiesCovered || '',
      zipcodesCovered: payload.zipcodesCovered || '',
      siteNumber: payload.siteNumber || '',
      networkPartner: payload.partnerOrganizationNumbers || payload.networkPartner || payload.networkPartnerOrganizationIds || [],
      numberOfPotentialSites: payload.numberOfPotentialSites ?? 0,
      expectedCorridorSize: payload.expectedCorridorSize ?? 0,
      mediaLocations: Array.isArray(payload.mediaLocations) ? payload.mediaLocations : [],
      mapLocations: Array.isArray(payload.mapLocations) ? payload.mapLocations : [],
      createdAt: payload.createdAt ?? 0,
      updatedAt: payload.updatedAt ?? 0,
    }
  }, [data, corridorNumber])

  // Resolve partner org names from IDs
  const partnerOrgs = useMemo(() => {
    if (!corridor || !orgsData) return []
    const orgs: any[] = ((orgsData as any)?.content?.organizations as any[]) || (orgsData as any)?.organizations || []
    const partnerIds = corridor.networkPartner || []
    return partnerIds
      .map((id: string) => orgs.find((o) => String(o.orgNumber) === String(id)))
      .filter(Boolean)
  }, [corridor, orgsData])

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError || !corridor) {
    log.warn('NetworkCorridorDetail: corridor not found', { corridorNumber })
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-destructive">Network corridor not found.</p>
        <Link to="/sensnet/network-corridors" className="text-sm text-primary underline">
          Back to Network Corridors
        </Link>
      </div>
    )
  }

  const createdDate = corridor.createdAt
    ? new Date(corridor.createdAt * 1000).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : null

  const counties = corridor.countiesCovered
    ? corridor.countiesCovered.split(',').map((s) => s.trim()).filter(Boolean)
    : []

  const zipcodes = corridor.zipcodesCovered
    ? corridor.zipcodesCovered.split(',').map((s) => s.trim()).filter(Boolean)
    : []

  // Build an OSM search URL from the first county if available
  const mapSearchQuery = counties.length > 0
    ? encodeURIComponent(`${counties[0]} County`)
    : zipcodes.length > 0
    ? encodeURIComponent(zipcodes[0])
    : null

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          iframe { break-inside: avoid; }
        }
      `}</style>

      <div className="space-y-6">

        {/* Back button + Header */}
        <div className="flex flex-col gap-3">
          <div>
            <Link to="/sensnet/network-corridors">
              <Button variant="ghost" size="sm" className="gap-2 -ml-2">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </Link>
          </div>
          <ProfileHeader
            title={corridor.name}
            subtitle={createdDate ? `Created: ${createdDate}` : undefined}
            badge="Active"
            subtitleIcon={<MapPin className="h-4 w-4" />}
          />
        </div>

        {/* Left: Name & Description + Potential Sites stacked | Right: Corridor Image */}
        <div className="grid grid-cols-1 md:grid-cols-2 items-stretch gap-6">
          {/* Left column: two stacked cards */}
          <div className="flex flex-col gap-6">
            <ProfileSection
              title="Name & Description"
              icon={<MapPin className="h-4 w-4" />}
              variant="dark"
            >
              <div className="space-y-4 p-5">
                <div>
                  <p className="text-xs text-muted-foreground">Corridor Name</p>
                  <p className="text-lg font-semibold">{corridor.name}</p>
                  <div className="mt-2 border-b border-slate-200" />
                </div>
                {corridor.description && (
                  <div>
                    <p className="text-xs text-muted-foreground">Description</p>
                    <p className="text-sm leading-relaxed">{corridor.description}</p>
                  </div>
                )}
              </div>
            </ProfileSection>

            <ProfileSection
              title="Potential Sites & Coverage Area"
              icon={<BarChart2 className="h-4 w-4" />}
              variant="dark"
            >
              <div className="grid grid-cols-2 divide-x divide-slate-200 p-5">
                <div className="pr-6">
                  <p className="text-xs text-muted-foreground">Number of Potential Sites</p>
                  <p className="text-3xl font-bold text-slate-800">
                    {corridor.numberOfPotentialSites ?? '—'}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">Sites planned in this corridor</p>
                </div>
                <div className="pl-6">
                  <p className="text-xs text-muted-foreground">Expected Corridor Area</p>
                  <p className="text-3xl font-bold text-slate-800">
                    {corridor.expectedCorridorSize ? `${corridor.expectedCorridorSize} mi²` : '—'}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">Estimated coverage area</p>
                </div>
              </div>
            </ProfileSection>
          </div>

          {/* Right column: corridor image stretches to full height of left column */}
          <ProfileSection title="Corridor Image" icon={<MapPin className="h-4 w-4" />} variant="dark" className="flex flex-col">
            <div className="flex-1 overflow-hidden bg-slate-100">
              {corridor.mediaLocations && corridor.mediaLocations.length > 0 ? (
                <img
                  src={corridor.mediaLocations[0]}
                  alt={corridor.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
                  <MapPin className="h-8 w-8 opacity-30" />
                  <span className="text-sm">No image available</span>
                </div>
              )}
            </div>
          </ProfileSection>
        </div>

        {/* Map */}
        <ProfileSection
          title="Coverage Map"
          icon={<Map className="h-4 w-4" />}
          variant="dark"
        >
          <div className="overflow-hidden" style={{ height: 360 }}>
            {mapSearchQuery ? (
              <iframe
                title={`Map for ${corridor.name}`}
                src={`https://www.openstreetmap.org/export/embed.html?bbox=-100,30,-80,50&layer=mapnik`}
                className="h-full w-full border-0"
              />
            ) : (
              <div className="flex h-full items-center justify-center bg-slate-100 text-sm text-muted-foreground">
                No coverage area coordinates available
              </div>
            )}
          </div>
          {(counties.length > 0 || zipcodes.length > 0) && (
            <div className="border-t border-slate-200 px-5 py-3">
              <p className="text-xs text-muted-foreground">
                Coverage spans {counties.length > 0 ? `${counties.length} count${counties.length === 1 ? 'y' : 'ies'}` : ''}
                {counties.length > 0 && zipcodes.length > 0 ? ' · ' : ''}
                {zipcodes.length > 0 ? `${zipcodes.length} zip code${zipcodes.length === 1 ? '' : 's'}` : ''}
              </p>
            </div>
          )}
        </ProfileSection>

        {/* Network Partner Information */}
        <ProfileSection
          title="Network Partner Information"
          icon={<Users className="h-4 w-4" />}
          variant="dark"
        >
          <div className="p-5">
            {partnerOrgs.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {partnerOrgs.map((org: any) => (
                  <div key={org.orgNumber} className="rounded-lg border border-slate-200 p-4">
                    <p className="font-medium">{org.name}</p>
                    {org.emailId && (
                      <p className="mt-1 text-xs text-muted-foreground">{org.emailId}</p>
                    )}
                    {org.phoneNumber && (
                      <p className="text-xs text-muted-foreground">{org.phoneNumber}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No network partners assigned</p>
            )}
          </div>
        </ProfileSection>

        {/* Counties Covered */}
        {counties.length > 0 && (
          <ProfileSection
            title="Counties Covered"
            icon={<MapPin className="h-4 w-4" />}
            variant="dark"
          >
            <div className="p-5">
              <div className="flex flex-wrap gap-2">
                {counties.map((county) => (
                  <span
                    key={county}
                    className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-800 border border-amber-200"
                  >
                    {county}
                  </span>
                ))}
              </div>
            </div>
          </ProfileSection>
        )}

        {/* ZIP Codes Covered */}
        {zipcodes.length > 0 && (
          <ProfileSection
            title="ZIP Codes Covered"
            icon={<MapPin className="h-4 w-4" />}
            variant="dark"
          >
            <div className="p-5">
              <div className="flex flex-wrap gap-2">
                {zipcodes.map((zip) => (
                  <span
                    key={zip}
                    className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-sm font-mono text-slate-700 border border-slate-200"
                  >
                    {zip}
                  </span>
                ))}
              </div>
            </div>
          </ProfileSection>
        )}

      </div>
    </>
  )
}
